
import apiClient from '../api/client';
import { debugLog } from '../../utils/debug';

// Audit event types
export enum AuditEventType {
  FILE_UPLOAD_START = 'file_upload_start',
  FILE_UPLOAD_PROGRESS = 'file_upload_progress',
  FILE_UPLOAD_COMPLETE = 'file_upload_complete',
  FILE_UPLOAD_ERROR = 'file_upload_error',
  FILE_PROCESSING_START = 'file_processing_start',
  FILE_PROCESSING_PROGRESS = 'file_processing_progress',
  FILE_PROCESSING_COMPLETE = 'file_processing_complete',
  FILE_PROCESSING_ERROR = 'file_processing_error',
  FILE_DOWNLOAD_START = 'file_download_start',
  FILE_DOWNLOAD_COMPLETE = 'file_download_complete',
  FILE_DELETE = 'file_delete',
  WORKFLOW_CREATE = 'workflow_create',
  WORKFLOW_UPDATE = 'workflow_update',
  CLIENT_CREATE = 'client_create',
  CLIENT_UPDATE = 'client_update',
  ANALYSIS_START = 'analysis_start',
  ANALYSIS_COMPLETE = 'analysis_complete',
  ERROR_RECOVERY_ATTEMPT = 'error_recovery_attempt',
  SYSTEM_ERROR = 'system_error'
}

// Audit event severity levels
export enum AuditSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

// File processing stages
export enum ProcessingStage {
  QUEUED = 'queued',
  UPLOADING = 'uploading',
  UPLOADED = 'uploaded',
  VALIDATING = 'validating',
  PARSING = 'parsing',
  PROCESSING = 'processing',
  ANALYZING = 'analyzing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  RETRY = 'retry'
}

// Audit event interface
export interface AuditEvent {
  id: string;
  timestamp: string;
  eventType: AuditEventType;
  severity: AuditSeverity;
  clientId: string;
  workflowId?: string;
  fileId?: string;
  fileName?: string;
  dataSource?: string;
  stage?: ProcessingStage;
  progress?: number;
  message: string;
  details?: Record<string, any>;
  errorCode?: string;
  errorStack?: string;
  userId?: string;
  sessionId: string;
  correlationId?: string;
  duration?: number;
  retryCount?: number;
}

// File operation status
export interface FileOperationStatus {
  fileId: string;
  fileName: string;
  clientId: string;
  workflowId: string;
  dataSource: string;
  stage: ProcessingStage;
  progress: number;
  startTime: string;
  lastUpdate: string;
  estimatedCompletion?: string;
  errorCount: number;
  retryCount: number;
  currentOperation?: string;
  totalBytes?: number;
  processedBytes?: number;
}

// Audit summary interface
export interface AuditSummary {
  totalEvents: number;
  errorCount: number;
  warningCount: number;
  activeOperations: number;
  averageUploadTime: number;
  averageProcessingTime: number;
  successRate: number;
  mostCommonErrors: Array<{ error: string; count: number }>;
  operationsByStage: Record<ProcessingStage, number>;
  recentActivity: AuditEvent[];
}

// Event listeners type
type AuditEventListener = (event: AuditEvent) => void;

/**
 * Audit Service for comprehensive file operation tracking and auditing
 */
export class AuditService {
  private static instance: AuditService;
  private sessionId: string;
  private eventCache: Map<string, AuditEvent> = new Map();
  private operationStatus: Map<string, FileOperationStatus> = new Map();
  private eventListeners: Map<AuditEventType, AuditEventListener[]> = new Map();
  private cacheKey = 'audit_events_cache';
  private maxCacheSize = 1000;
  private isOnline = navigator.onLine;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.setupOfflineHandling();
    this.loadCachedEvents();
    
    // Setup periodic cache cleanup
    setInterval(() => this.cleanupCache(), 5 * 60 * 1000); // Every 5 minutes
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): AuditService {
    if (!AuditService.instance) {
      AuditService.instance = new AuditService();
    }
    return AuditService.instance;
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Setup offline event handling
   */
  private setupOfflineHandling(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.syncCachedEvents();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  /**
   * Load cached events from local storage
   */
  private loadCachedEvents(): void {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (cached) {
        const events = JSON.parse(cached);
        events.forEach((event: AuditEvent) => {
          this.eventCache.set(event.id, event);
        });
      }
    } catch (error) {
      debugLog('AuditService', 'Error loading cached events', error);
    }
  }

  /**
   * Save events to local storage
   */
  private saveCachedEvents(): void {
    try {
      const events = Array.from(this.eventCache.values())
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, this.maxCacheSize);
      
      localStorage.setItem(this.cacheKey, JSON.stringify(events));
    } catch (error) {
      debugLog('AuditService', 'Error saving cached events', error);
    }
  }

  /**
   * Sync cached events to server when online
   */
  private async syncCachedEvents(): Promise<void> {
    if (!this.isOnline || this.eventCache.size === 0) return;

    try {
      const events = Array.from(this.eventCache.values());
      
      // Send each event individually to the new audit endpoints
      for (const event of events) {
        try {
          await apiClient.post('/api/v1/audit/events', {
            event_type: event.eventType,
            severity: event.severity.toUpperCase(),
            message: event.message,
            client_id: event.clientId,
            workflow_id: event.workflowId,
            file_id: event.fileId,
            metadata: {
              fileName: event.fileName,
              dataSource: event.dataSource,
              stage: event.stage,
              progress: event.progress,
              details: event.details,
              errorCode: event.errorCode,
              sessionId: event.sessionId,
              correlationId: event.correlationId,
              duration: event.duration,
              retryCount: event.retryCount
            }
          });
        } catch (eventError) {
          debugLog('AuditService', 'Error syncing individual event', eventError);
        }
      }
      
      // Clear synced events
      this.eventCache.clear();
      localStorage.removeItem(this.cacheKey);
      
      debugLog('AuditService', `Synced ${events.length} cached events`);
    } catch (error) {
      debugLog('AuditService', 'Error syncing cached events', error);
    }
  }

  /**
   * Cleanup old cached events
   */
  private cleanupCache(): void {
    const cutoffTime = Date.now() - (24 * 60 * 60 * 1000); // 24 hours ago
    let cleaned = 0;

    for (const [id, event] of this.eventCache.entries()) {
      if (new Date(event.timestamp).getTime() < cutoffTime) {
        this.eventCache.delete(id);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      this.saveCachedEvents();
      debugLog('AuditService', `Cleaned up ${cleaned} old cached events`);
    }
  }

  /**
   * Generate unique event ID
   */
  private generateEventId(): string {
    return `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Log an audit event
   */
  public async logEvent(
    eventType: AuditEventType,
    severity: AuditSeverity,
    message: string,
    context: {
      clientId: string;
      workflowId?: string;
      fileId?: string;
      fileName?: string;
      dataSource?: string;
      stage?: ProcessingStage;
      progress?: number;
      details?: Record<string, any>;
      errorCode?: string;
      errorStack?: string;
      correlationId?: string;
      duration?: number;
      retryCount?: number;
    }
  ): Promise<void> {
    const event: AuditEvent = {
      id: this.generateEventId(),
      timestamp: new Date().toISOString(),
      eventType,
      severity,
      message,
      sessionId: this.sessionId,
      ...context
    };

    // Cache event locally
    this.eventCache.set(event.id, event);
    this.saveCachedEvents();

    // Update operation status if applicable
    if (event.fileId) {
      this.updateOperationStatus(event);
    }

    // Notify listeners
    this.notifyListeners(event);

    // Send to server if online
    if (this.isOnline) {
      try {
        await apiClient.post('/api/v1/audit/events', {
          event_type: event.eventType,
          severity: event.severity.toUpperCase(),
          message: event.message,
          client_id: event.clientId,
          workflow_id: event.workflowId,
          file_id: event.fileId,
          metadata: {
            fileName: event.fileName,
            dataSource: event.dataSource,
            stage: event.stage,
            progress: event.progress,
            details: event.details,
            errorCode: event.errorCode,
            sessionId: event.sessionId,
            correlationId: event.correlationId,
            duration: event.duration,
            retryCount: event.retryCount
          }
        });
      } catch (error) {
        debugLog('AuditService', 'Error sending audit event to server', error);
        // Event is already cached for later sync
      }
    }

    debugLog('AuditService', `Logged ${eventType} event`, event);
  }

  /**
   * Update file operation status
   */
  private updateOperationStatus(event: AuditEvent): void {
    if (!event.fileId) return;

    const existing = this.operationStatus.get(event.fileId);
    const now = new Date().toISOString();

    const status: FileOperationStatus = {
      fileId: event.fileId,
      fileName: event.fileName || existing?.fileName || 'Unknown',
      clientId: event.clientId,
      workflowId: event.workflowId || existing?.workflowId || '',
      dataSource: event.dataSource || existing?.dataSource || '',
      stage: event.stage || existing?.stage || ProcessingStage.QUEUED,
      progress: event.progress ?? existing?.progress ?? 0,
      startTime: existing?.startTime || now,
      lastUpdate: now,
      errorCount: existing?.errorCount || 0,
      retryCount: event.retryCount ?? existing?.retryCount ?? 0,
      currentOperation: event.message,
      totalBytes: existing?.totalBytes,
      processedBytes: existing?.processedBytes
    };

    // Update error count
    if (event.severity === AuditSeverity.ERROR || event.severity === AuditSeverity.CRITICAL) {
      status.errorCount = (existing?.errorCount || 0) + 1;
    }

    // Calculate estimated completion for upload/processing stages
    if (event.progress && event.progress > 0 && event.progress < 100) {
      const elapsed = new Date().getTime() - new Date(status.startTime).getTime();
      const estimatedTotal = (elapsed / event.progress) * 100;
      const remaining = estimatedTotal - elapsed;
      status.estimatedCompletion = new Date(Date.now() + remaining).toISOString();
    }

    this.operationStatus.set(event.fileId, status);
  }

  /**
   * Add event listener
   */
  public addEventListener(eventType: AuditEventType, listener: AuditEventListener): void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, []);
    }
    this.eventListeners.get(eventType)!.push(listener);
  }

  /**
   * Remove event listener
   */
  public removeEventListener(eventType: AuditEventType, listener: AuditEventListener): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  /**
   * Notify event listeners
   */
  private notifyListeners(event: AuditEvent): void {
    const listeners = this.eventListeners.get(event.eventType);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {
          debugLog('AuditService', 'Error in event listener', error);
        }
      });
    }
  }

  /**
   * Get file operation status
   */
  public getFileOperationStatus(fileId: string): FileOperationStatus | undefined {
    return this.operationStatus.get(fileId);
  }

  /**
   * Get all active file operations
   */
  public getActiveOperations(): FileOperationStatus[] {
    return Array.from(this.operationStatus.values())
      .filter(status => 
        status.stage !== ProcessingStage.COMPLETED && 
        status.stage !== ProcessingStage.FAILED
      );
  }

  /**
   * Get recent audit events
   */
  public getRecentEvents(limit: number = 50): AuditEvent[] {
    return Array.from(this.eventCache.values())
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, limit);
  }

  /**
   * Get audit summary
   */
  public async getAuditSummary(
    clientId?: string,
    workflowId?: string,
    timeRange?: { start: string; end: string }
  ): Promise<AuditSummary> {
    try {
      const params = new URLSearchParams();
      if (clientId) params.append('client_id', clientId);
      if (workflowId) params.append('workflow_id', workflowId);
      if (timeRange) {
        params.append('start_time', timeRange.start);
        params.append('end_time', timeRange.end);
      }

      const response = await apiClient.get(`/api/v1/audit/summary?${params.toString()}`);
      return response.data;
    } catch (error) {
      debugLog('AuditService', 'Error getting audit summary', error);
      
      // Fallback to local data
      return this.generateLocalSummary();
    }
  }

  /**
   * Generate local audit summary from cached events
   */
  private generateLocalSummary(): AuditSummary {
    const events = Array.from(this.eventCache.values());
    const errorEvents = events.filter(e => e.severity === AuditSeverity.ERROR || e.severity === AuditSeverity.CRITICAL);
    const warningEvents = events.filter(e => e.severity === AuditSeverity.WARNING);
    const activeOps = this.getActiveOperations();

    // Count operations by stage
    const operationsByStage: Record<ProcessingStage, number> = {} as Record<ProcessingStage, number>;
    Object.values(ProcessingStage).forEach(stage => {
      operationsByStage[stage] = activeOps.filter(op => op.stage === stage).length;
    });

    // Find most common errors
    const errorCounts: Record<string, number> = {};
    errorEvents.forEach(event => {
      const key = event.errorCode || event.message;
      errorCounts[key] = (errorCounts[key] || 0) + 1;
    });

    const mostCommonErrors = Object.entries(errorCounts)
      .map(([error, count]) => ({ error, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalEvents: events.length,
      errorCount: errorEvents.length,
      warningCount: warningEvents.length,
      activeOperations: activeOps.length,
      averageUploadTime: 0, // Would need to calculate from upload events
      averageProcessingTime: 0, // Would need to calculate from processing events
      successRate: events.length > 0 ? (events.length - errorEvents.length) / events.length : 1,
      mostCommonErrors,
      operationsByStage,
      recentActivity: this.getRecentEvents(10)
    };
  }

  /**
   * Log file upload start
   */
  public async logFileUploadStart(
    clientId: string,
    workflowId: string,
    fileId: string,
    fileName: string,
    dataSource: string,
    fileSize?: number
  ): Promise<void> {
    await this.logEvent(
      AuditEventType.FILE_UPLOAD_START,
      AuditSeverity.INFO,
      `Starting upload of ${fileName}`,
      {
        clientId,
        workflowId,
        fileId,
        fileName,
        dataSource,
        stage: ProcessingStage.UPLOADING,
        progress: 0,
        details: { fileSize }
      }
    );
  }

  /**
   * Log file upload progress
   */
  public async logFileUploadProgress(
    clientId: string,
    workflowId: string,
    fileId: string,
    fileName: string,
    progress: number,
    bytesTransferred?: number,
    totalBytes?: number
  ): Promise<void> {
    await this.logEvent(
      AuditEventType.FILE_UPLOAD_PROGRESS,
      AuditSeverity.INFO,
      `Upload progress: ${progress}%`,
      {
        clientId,
        workflowId,
        fileId,
        fileName,
        stage: ProcessingStage.UPLOADING,
        progress,
        details: { bytesTransferred, totalBytes }
      }
    );
  }

  /**
   * Log file upload completion
   */
  public async logFileUploadComplete(
    clientId: string,
    workflowId: string,
    fileId: string,
    fileName: string,
    blobPath: string,
    duration?: number
  ): Promise<void> {
    await this.logEvent(
      AuditEventType.FILE_UPLOAD_COMPLETE,
      AuditSeverity.INFO,
      `Upload completed: ${fileName}`,
      {
        clientId,
        workflowId,
        fileId,
        fileName,
        stage: ProcessingStage.UPLOADED,
        progress: 100,
        duration,
        details: { blobPath }
      }
    );
  }

  /**
   * Log file upload error
   */
  public async logFileUploadError(
    clientId: string,
    workflowId: string,
    fileId: string,
    fileName: string,
    error: Error,
    retryCount: number = 0
  ): Promise<void> {
    await this.logEvent(
      AuditEventType.FILE_UPLOAD_ERROR,
      AuditSeverity.ERROR,
      `Upload failed: ${error.message}`,
      {
        clientId,
        workflowId,
        fileId,
        fileName,
        stage: ProcessingStage.FAILED,
        errorCode: error.name,
        errorStack: error.stack,
        retryCount,
        details: { error: error.message }
      }
    );
  }

  /**
   * Log processing stage change
   */
  public async logProcessingStageChange(
    clientId: string,
    workflowId: string,
    fileId: string,
    fileName: string,
    stage: ProcessingStage,
    progress?: number,
    message?: string
  ): Promise<void> {
    await this.logEvent(
      AuditEventType.FILE_PROCESSING_PROGRESS,
      AuditSeverity.INFO,
      message || `Processing stage: ${stage}`,
      {
        clientId,
        workflowId,
        fileId,
        fileName,
        stage,
        progress
      }
    );
  }

  /**
   * Clear operation status (when operation completes)
   */
  public clearOperationStatus(fileId: string): void {
    this.operationStatus.delete(fileId);
  }

  /**
   * Export audit data
   */
  public async exportAuditData(
    format: 'json' | 'csv' = 'json',
    filters?: {
      clientId?: string;
      workflowId?: string;
      eventTypes?: AuditEventType[];
      severity?: AuditSeverity[];
      timeRange?: { start: string; end: string };
    }
  ): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      
      if (filters) {
        if (filters.clientId) params.append('client_id', filters.clientId);
        if (filters.workflowId) params.append('workflow_id', filters.workflowId);
        if (filters.eventTypes) params.append('event_types', filters.eventTypes.join(','));
        if (filters.severity) params.append('severity', filters.severity.join(','));
        if (filters.timeRange) {
          params.append('start_time', filters.timeRange.start);
          params.append('end_time', filters.timeRange.end);
        }
      }

      const response = await apiClient.get(`/api/v1/audit/export?${params.toString()}`, {
        responseType: 'blob'
      });

      return response.data;
    } catch (error) {
      debugLog('AuditService', 'Error exporting audit data', error);
      throw error;
    }
  }
}

// Export singleton instance
export default AuditService.getInstance();
